import { Component, Input, OnInit, Optional } from "@angular/core";
import { UntilDestroy } from "@ngneat/until-destroy";

import { VDialogRef } from "@app/directives/_layout/v-dialog/v-dialog-ref";
import { IVehicle } from "@app/interfaces/IVehicle";
import { GroupService } from "@app/services/groups/group.service";
import { IGroupWithIds } from "../../_models/IGroupWithIds";
import { ResourcesAddedRemoved } from "../../_models/resources-added-removed";
import { getGroupChildrenRecursively as getGroupAndChildrenRecursively } from "../../utils/get-group-children-recursively";
import './group-save-confirmation.component.scss';

export interface GroupSaveConfirmation {
    originalGroup: IGroupWithIds;
    changedGroup: IGroupWithIds;
    vehiclesChanged: ResourcesAddedRemoved,
    vehicles: IVehicle[]
}

export interface GroupVehiclesModified {
    group: IGroupWithIds;
    vehiclesAdded: IVehicle[];
    vehiclesRemoved: IVehicle[];
}

@UntilDestroy()
@Component({
    selector: 'group-save-confirmation',
    template: require('./group-save-confirmation.component.html')
}) export class GroupSaveConfirmationComponent implements GroupSaveConfirmation, OnInit {
    @Input() originalGroup: IGroupWithIds;
    @Input() changedGroup: IGroupWithIds;
    @Input() vehiclesChanged: ResourcesAddedRemoved
    @Input() vehicles: IVehicle[]

    affectedGroups: GroupVehiclesModified[] = [];

    constructor(
        @Optional() private ref: VDialogRef, 
        private readonly groupService: GroupService
    ) {}

    ngOnInit(): void {
        this.initData();
    }

    confirm(): void {
        this.ref?.close(true);
    }

    cancel(): void {
        this.ref?.close(false);
    }

    private async initData(): Promise<void> {
        const added = this.getVehiclesAdded();
        const removed = this.getVehiclesRemoved();
        const groups = await this.groupService.getGroupsWithIds();

        // Distinct combined array of Groups from added and removed
        const affectedGroups = [...new Set([...added.keys(), ...removed.keys()])]
        .map(id => {
            if (id <= 0) {
                // if there is no valid id, we know it's current group (not present on all groups list yet)
                return {...this.originalGroup, Name: this.changedGroup.Name}; 
            }

            return groups.find(g => g.Id === id);
        });

        this.affectedGroups = affectedGroups.map(group => {
            return {
                group,
                vehiclesAdded: added.get(group.Id),
                vehiclesRemoved: removed.get(group.Id),
            }
        });
    }

    private getVehiclesAdded(): Map<number, IVehicle[]> {
        const added = this.vehicles.filter(v => this.vehiclesChanged.added.includes(v.Id));

        if (added.length === 0) {
            return new Map();
        }

        const groupsWithVehicles = new Map<number, IVehicle[]>();

        let currentGroup = this.originalGroup;

        while (!!currentGroup) {
            const addedToCurrentGroup = added.filter(v => !currentGroup.VehicleIds.includes(v.Id));

            if (addedToCurrentGroup.length !== 0) {
                groupsWithVehicles.set(currentGroup.Id, addedToCurrentGroup);
            }

            currentGroup = currentGroup.parent;
        }

        return groupsWithVehicles;
    }

    private getVehiclesRemoved(): Map<number, IVehicle[]> {
        const removed = this.vehicles.filter(v => this.vehiclesChanged.removed.includes(v.Id));

        if (removed.length === 0) {
            return new Map();
        }

        const groupsWithVehicles = new Map<number, IVehicle[]>();

        getGroupAndChildrenRecursively(this.originalGroup, (g) => {
            const removedFromCurrentGroup = removed.filter(v => g.VehicleIds.includes(v.Id));

            if (removedFromCurrentGroup.length !== 0) {
                groupsWithVehicles.set(g.Id, removedFromCurrentGroup);
            }
        });

        return groupsWithVehicles;
    }
}